import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { TopNav } from "../components/TopNav";
import { PromptBar } from "../components/PromptBar";
import { DestinationCard } from "../components/DestinationCard";
import { Sparkles, Flame, Compass, ArrowRight } from "lucide-react";
import { fetchFeatured } from "../lib/api";

// Rotating hero pool — cinematic creator / Gen Z travel aesthetic.
// A different one is chosen on every page load for a fresh feel.
const HERO_POOL = [
  "https://images.unsplash.com/photo-1762535120786-76238d9eeb0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  "https://images.unsplash.com/photo-1530789253388-582c481c54b0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920", // Santorini sunset cliffs
  "https://images.unsplash.com/photo-1542051841857-5f90071e7989?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920", // Tokyo neon street
  "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920", // Paris Eiffel evening
  "https://images.unsplash.com/photo-1518391846015-55a9cc003b25?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920", // Bali rice terraces
  "https://images.unsplash.com/photo-1508739773434-c26b3d09e071?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920", // Iceland blue lagoon
  "https://images.unsplash.com/photo-1539635278303-d4002c07eae3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920", // Marrakech alley
  "https://images.unsplash.com/photo-1533105079780-92b9be482077?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920", // Amalfi coast
  "https://images.unsplash.com/photo-1493558103817-58b2924bce98?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920", // Dubai skyline
  "https://images.unsplash.com/photo-1496588152823-86ff7695e68f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920", // NYC creator
];

export default function Landing() {
  const [featured, setFeatured] = useState([]);

  // Pick a fresh hero image per page load; useMemo keeps it stable during this render.
  const heroBg = useMemo(
    () => HERO_POOL[Math.floor(Math.random() * HERO_POOL.length)],
    []
  );

  useEffect(() => {
    fetchFeatured().then(setFeatured).catch(() => setFeatured([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopNav />

      {/* HERO */}
      <section className="relative min-h-[100svh] w-full overflow-hidden">
        <div className="absolute inset-0">
          <motion.img
            key={heroBg}
            src={heroBg}
            alt="cinematic creator"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.2, 0.7, 0.2, 1] }}
            className="h-full w-full object-cover"
            data-testid="landing-hero-bg"
          />
          <div className="absolute inset-0 bg-black/55" />
          <div className="absolute inset-0 grain" />
          <div className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-[#FE0979]/20 blur-3xl" />
          <div className="absolute -bottom-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#00F2FE]/15 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8 pt-32 pb-12 min-h-[100svh] flex flex-col justify-end sm:justify-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
            className="space-y-5"
          >
            <span className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-200">
              <Sparkles className="h-3 w-3 text-[#00F2FE]" /> Turn Viral Inspiration Into Real Trips
            </span>
            <h1 className="font-display font-black tracking-[-0.02em] leading-[0.95] text-5xl sm:text-7xl lg:text-8xl">
              Turn viral travel<br />
              <span className="text-gradient">inspiration</span><br />
              into real trips.
            </h1>
            <p className="font-body text-zinc-300 text-base sm:text-lg max-w-xl">
              Discover viral spots, hidden gems and reel-ready itineraries powered by AI.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.2, 0.7, 0.2, 1] }}
            className="mt-7"
          >
            <PromptBar autoFocus />
            <div className="mt-3 text-[11px] text-zinc-400">
              Want more control?{" "}
              <a href="/create" data-testid="open-builder" className="text-[#00F2FE] hover:text-white underline-offset-2 hover:underline transition">
                Open the detailed builder →
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="relative z-10 px-0 sm:px-8 py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-0 mb-6 flex items-end justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#FE0979] mb-2 flex items-center gap-2">
              <Flame className="h-3.5 w-3.5" /> Trending Vibes
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">Currently going viral</h2>
          </div>
          <a href="#more" className="hidden sm:inline-flex text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 hover:text-white items-center gap-2">
            Explore <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>

        <div className="snap-x-mandatory no-scrollbar overflow-x-auto flex gap-4 px-5 sm:px-8 pb-4">
          {featured.map((item, i) => (
            <DestinationCard key={item.id} item={item} index={i} />
          ))}
          {featured.length === 0 && (
            <div className="text-zinc-500 px-6 py-12">Loading vibes…</div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="more" className="relative z-10 px-5 sm:px-8 py-14 sm:py-24">
        <div className="mx-auto max-w-6xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#00F2FE] mb-2 flex items-center gap-2">
            <Compass className="h-3.5 w-3.5" /> The flow
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl">From a vibe in your head, to a viral reel in your camera roll.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { n: "01", t: "Drop a vibe", d: "\"Old-money Italy\". \"Tokyo neon\". \"Bali jungle dawn\". Anything." },
              { n: "02", t: "Get a creator itinerary", d: "Hidden spots, viral scores, best times, captions, photo angles." },
              { n: "03", t: "Shoot. Post. Go viral.", d: "Tap any spot for the angle. Open in Google Maps. Done." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.55, delay: i * 0.1, ease: [0.2, 0.7, 0.2, 1] }}
                className="glass rounded-3xl p-6 sm:p-8 hover:border-white/20 transition"
              >
                <div className="font-display font-black text-5xl text-gradient">{s.n}</div>
                <h3 className="mt-3 font-display font-bold text-xl">{s.t}</h3>
                <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 px-5 sm:px-8 pb-20">
        <div className="mx-auto max-w-5xl glass-strong rounded-[28px] p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute -top-40 -left-20 h-[400px] w-[400px] rounded-full bg-[#FE0979]/15 blur-3xl" />
          <div className="absolute -bottom-40 -right-20 h-[400px] w-[400px] rounded-full bg-[#00F2FE]/12 blur-3xl" />
          <h2 className="relative font-display font-black text-3xl sm:text-5xl tracking-tight">Your next reel<br /><span className="text-gradient">is one prompt away.</span></h2>
          <p className="relative mt-3 text-zinc-300 max-w-xl mx-auto text-sm sm:text-base">No more 4-hour Pinterest deep dives. Drop a vibe and we'll do the location scouting.</p>
          <div className="relative mt-7 max-w-2xl mx-auto">
            <PromptBar />
          </div>
        </div>
      </section>

      <footer className="px-5 py-10 text-center text-xs text-zinc-500 font-body">
        Made for creators · VYBE © 2026
      </footer>
    </div>
  );
}
